import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MAIN_CONTENT_ID } from '@/lib/constants/layout';

const HASH_PREFIX_LENGTH = 1;

function decodeHash(hash: string): string {
  const raw = hash.slice(HASH_PREFIX_LENGTH);
  try {
    return decodeURIComponent(raw);
  } catch {
    // A malformed escape (`%E0%A4%A`) cannot name an element; use it verbatim.
    return raw;
  }
}

function scrollToTop(): void {
  window.scrollTo(0, 0);
  document.getElementById(MAIN_CONTENT_ID)?.scrollTo(0, 0);
}

/**
 * One scroll reset for the whole app (MYN-275, FR-182). Mounted once inside
 * `BrowserRouter` (`main.tsx`), so every shell - public, app, admin console,
 * shared profile - and the standalone routes are covered with no per-layout
 * copy. `BrowserRouter` is a non-data router, so `ScrollRestoration` is not
 * available.
 *
 * - A new pathname resets the window and `#main-content` (the admin console
 *   scrolls inside that element, not the window).
 * - A hash scrolls that element into view; a missing target falls back to the top.
 * - A query-only change (filters, search, paging) neither re-runs the effect nor
 *   moves the page: the effect depends on `pathname` and `hash`, not `search`.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const target = document.getElementById(decodeHash(hash));
      if (target) {
        target.scrollIntoView();
        return;
      }
    }
    scrollToTop();
  }, [pathname, hash]);

  return null;
}
